import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh: <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    map:     <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
    globe:   <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    ban:     <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>,
    alert:   <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    mail:    <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    pin:     <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
    flag:    <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>,
    shield:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    search:  <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    wifi:    <><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

const countryFlags = { FR: '🇫🇷', GB: '🇬🇧', DE: '🇩🇪', ES: '🇪🇸', MA: '🇲🇦', SN: '🇸🇳', US: '🇺🇸' };

export default function MapPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get('/track/events?limit=500');
      setEvents(data.filter(e => e.lat && e.lon));
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (loading || !mapRef.current) return;
    if (!window.L) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.head.appendChild(script);
    } else {
      initMap();
    }
    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, [loading, events, filter]);

  function initMap() {
    if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    if (!mapRef.current || !window.L) return;
    const L = window.L;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false }).setView([46.5, 2.5], 5);
    mapInstanceRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18 }).addTo(map);
    const locations = {};
    const filtered = events.filter(e => {
      if (filter === 'fr') return e.country === 'FR' || !e.country;
      if (filter === 'abroad') return e.country && e.country !== 'FR';
      if (filter === 'suspect') return e.event_type === 'unauthorized_access' || (e.country && e.country !== 'FR');
      return true;
    });
    filtered.forEach(e => {
      const key = `${parseFloat(e.lat).toFixed(2)}_${parseFloat(e.lon).toFixed(2)}`;
      if (!locations[key]) locations[key] = { lat: parseFloat(e.lat), lon: parseFloat(e.lon), events: [], city: e.city, country: e.country };
      locations[key].events.push(e);
    });
    Object.values(locations).forEach(loc => {
      const hasUnauth = loc.events.some(e => e.event_type === 'unauthorized_access');
      const abroad = loc.country && loc.country !== 'FR';
      const isSuspect = hasUnauth || abroad;
      const count = loc.events.length;
      const members = [...new Set(loc.events.map(e => e.member_email).filter(Boolean))];
      const color = isSuspect ? '#ff4444' : '#d4f535';
      const textColor = isSuspect ? '#fff' : '#000';
      const size = Math.max(24, Math.min(50, 24 + count * 3));
      const icon = L.divIcon({
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:3px solid rgba(255,255,255,0.3);display:flex;align-items:center;justify-content:center;color:${textColor};font-weight:900;font-size:${size > 35 ? 13 : 11}px;box-shadow:0 0 16px ${color}88;${isSuspect ? 'animation:blink 1.5s infinite;' : ''}">${count}</div><style>@keyframes blink{0%,100%{opacity:1}50%{opacity:0.5}}</style>`,
        className: '', iconSize: [size, size], iconAnchor: [size/2, size/2],
      });
      const flag = countryFlags[loc.country] || '🌍';
      const popup = `<div style="font-family:-apple-system,sans-serif;min-width:190px;background:#111;color:#fff;border-radius:10px;padding:12px"><div style="font-weight:800;font-size:14px;margin-bottom:6px;color:#ffffff">${flag} ${loc.city || loc.country || 'Inconnu'}</div><div style="font-size:12px;color:#d4f535;margin-bottom:6px;font-weight:700">${count} accès</div>${isSuspect ? '<div style="background:#ff444422;color:#ff4444;border:1px solid #ff4444;border-radius:6px;padding:4px 8px;font-size:11px;font-weight:700;margin-bottom:6px">⛔ Suspect</div>' : ''}<div style="font-size:11px;color:#aaa">${members.map(m => `<div>✉ ${m}</div>`).join('')}</div></div>`;
      L.marker([loc.lat, loc.lon], { icon }).addTo(map).bindPopup(popup, { className: 'dark-popup' });
    });
    if (Object.keys(locations).length > 0) {
      const lats = Object.values(locations).map(l => l.lat);
      const lons = Object.values(locations).map(l => l.lon);
      map.fitBounds([[Math.min(...lats)-1, Math.min(...lons)-1],[Math.max(...lats)+1, Math.max(...lons)+1]]);
    }
  }

  const locationList = {};
  events.forEach(e => {
    const key = e.city || e.country || e.ip || 'Inconnu';
    if (!locationList[key]) locationList[key] = { city: e.city, country: e.country, ip: e.ip, events: [], members: new Set(), hasUnauth: false };
    locationList[key].events.push(e);
    if (e.member_email) locationList[key].members.add(e.member_email);
    if (e.event_type === 'unauthorized_access') locationList[key].hasUnauth = true;
  });
  const locs = Object.values(locationList);
  const isFrance = l => l.country === 'FR' || !l.country;
  const isAbroad = l => l.country && l.country !== 'FR';
  const filteredLocs = locs.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.city||''). toLowerCase().includes(s) || (l.country||'').toLowerCase().includes(s) || (l.ip||'').toLowerCase().includes(s) || [...l.members].some(m => m.toLowerCase().includes(s));
  });

  const filters = [
    { val: 'all',     label: 'Tout',      icon: 'globe' },
    { val: 'fr',      label: 'France',    icon: 'flag'  },
    { val: 'abroad',  label: 'Étranger',  icon: 'wifi'  },
    { val: 'suspect', label: 'Suspects',  icon: 'alert' },
  ];

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>
      <style>{`.dark-popup .leaflet-popup-content-wrapper{background:#111;border:1px solid #333;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.8);padding:0}.dark-popup .leaflet-popup-content{margin:0}.dark-popup .leaflet-popup-tip{background:#111}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: '#ffffff', letterSpacing: -0.5, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="map" size={20} color="#d4f535" />
            Carte des <span style={{ color: '#d4f535', marginLeft: 6 }}>connexions</span>
          </h1>
          <p style={{ color: '#666', fontSize: 12, marginTop: 3 }}>{events.length} accès géolocalisés</p>
        </div>
        <button onClick={fetchEvents} style={{ width: 40, height: 40, borderRadius: 10, border: '1.5px solid #d4f535', background: '#1a1a00', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="refresh" size={16} color="#d4f535" />
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: '4px solid #4db8ff', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ marginBottom: 8 }}><Icon name="flag" size={20} color="#4db8ff" /></div>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#4db8ff', lineHeight: 1 }}>{locs.filter(isFrance).length}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontWeight: 600 }}>Localisations France</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: `4px solid ${locs.filter(isAbroad).length > 0 ? '#ff4444' : '#333'}`, borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ marginBottom: 8 }}><Icon name="globe" size={20} color={locs.filter(isAbroad).length > 0 ? '#ff4444' : '#444'} /></div>
          <div style={{ fontSize: 26, fontWeight: 900, color: locs.filter(isAbroad).length > 0 ? '#ff4444' : '#444', lineHeight: 1 }}>{locs.filter(isAbroad).length}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontWeight: 600 }}>Pays étrangers</div>
        </div>
      </div>

      {/* Recherche */}
      <div style={{ marginBottom: 10, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
          <Icon name="search" size={14} color="#555" />
        </div>
        <input type="text" placeholder="Rechercher par email, ville, pays ou IP..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px 10px 36px', borderRadius: 10, border: '1.5px solid #333', fontSize: 13, outline: 'none', background: '#111', color: '#ffffff' }} />
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {filters.map(f => (
          <button key={f.val} onClick={() => setFilter(f.val)} style={{
            padding: '7px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            border: filter === f.val ? 'none' : '1.5px solid #333',
            background: filter === f.val ? '#d4f535' : '#111',
            color: filter === f.val ? '#000' : '#888',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Icon name={f.icon} size={11} color={filter === f.val ? '#000' : '#888'} />
            {f.label}
          </button>
        ))}
      </div>

      {/* Carte */}
      <div style={{ background: '#111', borderRadius: 14, border: '1px solid #222', overflow: 'hidden', marginBottom: 14, position: 'relative', zIndex: 0 }}>
        {loading ? (
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>Chargement...</div>
        ) : (
          <div ref={mapRef} style={{ height: 320, width: '100%' }} />
        )}
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#aaa', fontWeight: 600 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#d4f535', boxShadow: '0 0 8px #d4f53599' }} />
          Accès normal
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#aaa', fontWeight: 600 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff4444', boxShadow: '0 0 8px #ff444499' }} />
          Suspect
        </div>
      </div>

      {/* Liste */}
      <div style={{ marginBottom: 10, fontSize: 12, color: '#555' }}>
        {filteredLocs.length} localisation(s) affichée(s) sur {locs.length}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filteredLocs.sort((a,b) => b.events.length - a.events.length).map((l, i) => {
          const isSuspect = l.hasUnauth || isAbroad(l);
          const flag = countryFlags[l.country];
          return (
            <div key={i} style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: `4px solid ${isSuspect ? '#ff4444' : '#d4f535'}`, borderRadius: 12, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: isSuspect ? '#ff444422' : '#d4f53522', border: `2px solid ${isSuspect ? '#ff4444' : '#d4f535'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: flag ? 20 : 0 }}>
                  {flag
                    ? <span style={{ fontSize: 20 }}>{flag}</span>
                    : <Icon name={isSuspect ? 'alert' : 'pin'} size={18} color={isSuspect ? '#ff4444' : '#d4f535'} />
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#ffffff' }}>{l.city || l.country || l.ip || 'Inconnu'}</div>
                  {l.ip && <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', marginTop: 2 }}>{l.ip}</div>}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                    {[...l.members].map((m, j) => (
                      <span key={j} style={{ background: '#4db8ff22', color: '#4db8ff', border: '1px solid #4db8ff66', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Icon name="mail" size={9} color="#4db8ff" /> {m}
                      </span>
                    ))}
                  </div>
                  {isSuspect && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#ff4444', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Icon name="ban" size={11} color="#ff4444" />
                      {l.hasUnauth ? 'Tentative non autorisée' : 'Connexion étrangère'}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: isSuspect ? '#ff4444' : '#d4f535', lineHeight: 1 }}>{l.events.length}</div>
                  <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>accès</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
